// src/components/user/express/ExpressDeliveryPage.jsx
import React from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useExpressDelivery } from './useExpressDelivery';
import { ExpressStepIndicator } from './ExpressStepIndicator';
import { Step1Pickup }   from './Step1Pickup';
import { Step2Dropoff }  from './Step2Dropoff';
import { Step3Vehicle }  from './Step3Vehicle';
import { Step4Products } from './Step4Products';

export function ExpressDeliveryPage({ userId, setActiveComponent }) {
  const ex = useExpressDelivery(userId);
  // ── Success screen ────────────────────────────────────────
  if (ex.submitSuccess) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white px-6 text-center gap-5">
        <div className="w-20 h-20 rounded-full bg-brand-green/10 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-brand-green" />
        </div>
        <div>
          <h2 className="text-xl font-extrabold text-slate-900 font-montserrat mb-2">
            ¡Delivery Express creado!
          </h2>
          <p className="text-sm text-slate-500 font-montserrat">
            Tu pedido está pendiente de asignación de conductor.
          </p>
        </div>
        <button
          onClick={() => setActiveComponent('Analytics')}
          className="mt-4 bg-brand-primary text-white px-8 py-3 rounded-xl font-montserrat font-bold text-sm hover:bg-brand-primary/90 transition-colors shadow-md"
        >
          Volver a Analítica
        </button>
      </div>
    );
  }

  // ── Step content map ──────────────────────────────────────
  const stepContent = {
    1: (
      <Step1Pickup
        savedAddresses={ex.savedAddresses}
        pickupAddress={ex.pickupAddress}
        setPickupAddress={ex.setPickupAddress}
        setPickupLat={ex.setPickupLat}
        setPickupLng={ex.setPickupLng}
      />
    ),
    2: (
      <Step2Dropoff
        dropoffAddress={ex.dropoffAddress}
        setDropoffAddress={ex.setDropoffAddress}
        setDropoffLat={ex.setDropoffLat}
        setDropoffLng={ex.setDropoffLng}
        recipientName={ex.recipientName}
        setRecipientName={ex.setRecipientName}
        recipientPhone={ex.recipientPhone}
        setRecipientPhone={ex.setRecipientPhone}
      />
    ),
    3: (
      <Step3Vehicle
        pricingConfig={ex.pricingConfig}
        vehicleType={ex.vehicleType}
        setVehicleType={ex.setVehicleType}
        routeResult={ex.routeResult}
        calculatingRoute={ex.calculatingRoute}
        routeError={ex.routeError}
        calculateRoute={ex.calculateRoute}
        pickupLat={ex.pickupLat}
        pickupLng={ex.pickupLng}
        dropoffLat={ex.dropoffLat}
        dropoffLng={ex.dropoffLng}
      />
    ),
    4: (
      <Step4Products
        userId={userId}
        items={ex.items}
        setItems={ex.setItems}
        locations={ex.locations}
        charge={ex.charge}
        setCharge={ex.setCharge}
      />
    ),
  };

  const isLastStep = ex.currentStep === 4;

  const canProceed = {
    1: ex.canProceedStep1,
    2: ex.canProceedStep2,
    3: ex.canProceedStep3,
    4: ex.canProceedStep4,
  }[ex.currentStep];

  return (
    <div className="h-screen flex flex-col bg-white">

      {/* ── Header ── */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm z-20">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => {
              if (ex.currentStep === 1) setActiveComponent('Analytics');
              else ex.setCurrentStep(s => s - 1);
            }}
            className="flex items-center gap-1.5 text-slate-600 hover:text-brand-primary transition-colors font-montserrat text-sm"
          >
            <ChevronLeft className="w-5 h-5" />
            {ex.currentStep === 1 ? 'Volver' : 'Atrás'}
          </button>

          <h1 className="text-base font-extrabold text-brand-primary font-montserrat italic">
            FAST MAX Express
          </h1>

          <div className="w-16" /> {/* spacer */}
        </div>

        {/* Step indicator */}
        <div className="max-w-lg mx-auto">
          <ExpressStepIndicator currentStep={ex.currentStep} />
        </div>
      </div>

      {/* ── Scrollable step content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-4 py-6">
          {stepContent[ex.currentStep]}
        </div>
      </div>

      {/* ── Footer: Next / Confirm ── */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 px-4 py-4">
        <div className="max-w-lg mx-auto space-y-2">

          {/* Submit error */}
          {ex.submitError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600 font-montserrat">{ex.submitError}</p>
            </div>
          )}

          {isLastStep ? (
            <button
              onClick={ex.submitDelivery}
              disabled={!canProceed || ex.submitting}
              className={`w-full py-4 rounded-xl font-montserrat font-extrabold text-sm transition-all flex items-center justify-center gap-2
                ${canProceed && !ex.submitting
                  ? 'bg-brand-green text-white hover:bg-brand-green/90 shadow-lg'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
            >
              {ex.submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando delivery…</>
                : <><CheckCircle2 className="w-4 h-4" /> Confirmar Delivery Express</>
              }
            </button>
          ) : (
            <button
              onClick={() => ex.setCurrentStep(s => s + 1)}
              disabled={!canProceed}
              className={`w-full py-4 rounded-xl font-montserrat font-extrabold text-sm transition-all flex items-center justify-center gap-2
                ${canProceed
                  ? 'bg-brand-primary text-white hover:bg-brand-primary/90 shadow-lg'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
            >
              Continuar
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

    </div>
  );
}

export default ExpressDeliveryPage;